import { useEffect, useImperativeHandle } from 'react';
import { usePinchZoom } from '../../application/motion/usePinchZoom';

/**
 * One take in the full-screen viewer: fitted to the screen, and every way of
 * moving it (SPEC-012 PR-12.3).
 *
 * Its own component because the gesture state is PER PICTURE — each take has its
 * own scale, its own offset and its own bounds — and a hook per slide is what
 * keeps that true without the viewer above holding an array of nine views it
 * would then have to index correctly on every pointer frame.
 *
 * What it hands back up is deliberately thin: a `reset` and a `toggleZoom` the
 * viewer can aim at whichever take is showing, and one bit saying whether this
 * picture is currently fitted. Everything else stays down here, off React's
 * render path.
 */
export default function CarViewerSlide({
    source,
    alt,
    caption,
    index,
    railRef,
    controlsRef,
    onZoomChange,
}) {
    const { frameRef, pictureRef, fitted, reset, toggleZoom, handlers } = usePinchZoom({ railRef });

    useImperativeHandle(controlsRef, () => ({ reset, toggleZoom }), [reset, toggleZoom]);

    useEffect(() => {
        onZoomChange(index, fitted);
    }, [fitted, index, onZoomChange]);

    return (
        // Focusable, so the keys the hook provides have somewhere to arrive. This
        // is not a scroll container — the arrow keys a scroller would have given
        // for free are handed back by `onKeyDown` (SPEC-012 D-6).
        <li
            className={`car-viewer__slide${fitted ? '' : ' car-viewer__slide--zoomed'}`}
            ref={frameRef}
            tabIndex={0}
            {...handlers}
        >
            <img
                className="car-viewer__art"
                ref={pictureRef}
                src={source.src}
                srcSet={source.srcSet}
                /* The largest rung the record has, always: fit is the SMALLEST
                   this picture is ever drawn here, and a pinch immediately asks
                   for more than the file holds (SPEC-012 D-2). */
                sizes="100vw"
                alt={alt}
                decoding="async"
                /* The gesture on this picture is a pan; a native image drag would
                   end it halfway through and hand back a file instead. */
                draggable={false}
            />

            {/* The take, printed — the same thing the `alt` says for somebody who
                cannot see the picture. This is for somebody who can see it and
                still cannot tell a front three-quarter from a low one. */}
            <p className="car-viewer__caption">{caption}</p>
        </li>
    );
}
